/**
 * Dev Console — Fias Forge (ADR-023).
 *
 * The admin view of the hosted build environments: the two platform switches
 * (the Forge kill switch, fail-closed; the metering switch, default ON), the
 * fleet metrics, per-user holds, and the forges table with force-stop and
 * archive. Every write is admin-only server-side and lands a security-audit
 * row naming the acting admin (`routes/admin/forge.ts`).
 *
 * Force stop is not the owner's graceful stop: it kills the task at the
 * driver and finalizes the row, so anything the runtime had not snapshotted
 * is gone — the confirm dialog says so.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  api,
  type AdminForgeListParams,
  type AdminForgeMetrics,
  type ForgeStatus,
  type ForgeSummary,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';

const FORGE_STATUSES: readonly ForgeStatus[] = [
  'provisioning',
  'running',
  'stopping',
  'stopped',
  'archived',
  'failed',
];

/** Statuses the pipeline's `markForgeStopped(id, null)` may leave from. */
const FORCE_STOPPABLE: ReadonlySet<ForgeStatus> = new Set([
  'provisioning',
  'running',
  'stopping',
  'failed',
]);
/** Statuses `archiveForge` may leave from. */
const ARCHIVABLE: ReadonlySet<ForgeStatus> = new Set(['stopped', 'failed']);

const PAGE_SIZE = 50;

export function formatHours(seconds: number): string {
  return `${(seconds / 3600).toFixed(1)} h`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

const fmtCredits = (v: number) => v.toFixed(2);
const fmtWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const shortId = (id: string) => `${id.slice(0, 12)}…`;

function statusVariant(status: ForgeStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'running':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'archived':
      return 'outline';
    default:
      return 'secondary';
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ============================================================================
// Switches
// ============================================================================

function SwitchesCard() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: queryKeys.adminForge.settings(),
    queryFn: () => api.adminForgeGetSettings(),
  });
  const metering = useQuery({
    queryKey: queryKeys.adminForge.metering(),
    queryFn: () => api.adminForgeGetMetering(),
  });

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.adminForgeSetEnabled(enabled),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.adminForge.settings(), data);
      toast({ title: data.enabled ? 'Forge enabled' : 'Forge disabled' });
    },
    onError: (err) =>
      toast({
        title: 'Could not change the switch',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  const setMetering = useMutation({
    mutationFn: (enabled: boolean) => api.adminForgeSetMetering(enabled),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.adminForge.metering(), data);
      toast({ title: data.enabled ? 'Metering enabled' : 'Metering disabled' });
    },
    onError: (err) =>
      toast({
        title: 'Could not change metering',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform switches</CardTitle>
        <CardDescription>
          Both are audited. The kill switch is fail-closed (absent = off); metering is fail-open
          (absent = on) so a missing row can never stop charging.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="forge-enabled">Forge enabled</Label>
            <p className="text-xs text-muted-foreground">
              Off refuses new starts, new turns and every model-gateway request; running forges lose
              their control plane on the next poll.
            </p>
          </div>
          <Switch
            id="forge-enabled"
            aria-label="Forge enabled"
            checked={settings.data?.enabled === true}
            disabled={settings.isLoading || setEnabled.isPending}
            onCheckedChange={(checked) => setEnabled.mutate(checked)}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="forge-metering">Metering enabled</Label>
            <p className="text-xs text-muted-foreground">
              The daily job that charges compute and snapshot storage. Off leaves ledger rows
              unsettled; they are charged when it is turned back on.
            </p>
          </div>
          <Switch
            id="forge-metering"
            aria-label="Metering enabled"
            checked={metering.data?.enabled === true}
            disabled={metering.isLoading || setMetering.isPending}
            onCheckedChange={(checked) => setMetering.mutate(checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Metrics
// ============================================================================

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums" data-testid={`tile-${label}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function MetricsTiles({ metrics }: { metrics: AdminForgeMetrics | undefined }) {
  if (!metrics) return <p className="text-sm text-muted-foreground">Loading metrics…</p>;
  const c = metrics.countsByStatus;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          label="Running now"
          value={String(metrics.runningNow)}
          hint={`${metrics.openTurns} open turns`}
        />
        <Tile
          label="Active today"
          value={formatHours(metrics.activeSecondsToday)}
          hint={`${formatHours(metrics.activeSecondsMonth)} this month`}
        />
        <Tile
          label="Credits charged (30d)"
          value={fmtCredits(metrics.creditsCharged30d.total)}
          hint={`compute ${fmtCredits(metrics.creditsCharged30d.compute)} · storage ${fmtCredits(metrics.creditsCharged30d.storage)}`}
        />
        <Tile label="Snapshots retained" value={formatBytes(metrics.snapshotBytesTotal)} />
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {FORGE_STATUSES.map((s) => (
          <Badge key={s} variant={statusVariant(s)}>
            {s}: {c[s] ?? 0}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Holds
// ============================================================================

function HoldsCard() {
  const qc = useQueryClient();
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const holds = useQuery({
    queryKey: queryKeys.adminForge.holds(),
    queryFn: () => api.adminForgeListHolds(),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: queryKeys.adminForge.holds() });

  const create = useMutation({
    mutationFn: () => api.adminForgeCreateHold(userId.trim(), reason.trim()),
    onSuccess: (data) => {
      toast({ title: `Hold placed on ${data.hold.userId}` });
      setUserId('');
      setReason('');
      void invalidate();
    },
    onError: (err) =>
      toast({
        title: 'Could not create hold',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  const release = useMutation({
    mutationFn: (heldUserId: string) => api.adminForgeReleaseHold(heldUserId),
    onSuccess: (data) => {
      toast({ title: `Hold released for ${data.userId}` });
      void invalidate();
    },
    onError: (err) =>
      toast({
        title: 'Could not release hold',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const canCreate = userId.trim().length > 0 && reason.trim().length > 0 && !create.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>User holds</CardTitle>
        <CardDescription>
          A held user cannot start a forge or send a turn (403 FORGE_USER_HELD). Distinct from the
          automatic billing hold the metering job sets when a charge fails.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate) create.mutate();
          }}
        >
          <div>
            <Label htmlFor="hold-user">User id</Label>
            <Input
              id="hold-user"
              value={userId}
              maxLength={64}
              placeholder="usr_…"
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="hold-reason">Reason (audited)</Label>
            <Input
              id="hold-reason"
              value={reason}
              maxLength={500}
              placeholder="Why this user is held"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={!canCreate}>
            Place hold
          </Button>
        </form>

        {holds.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading holds…</p>
        ) : (holds.data?.holds.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">No users are held.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-2">User</th>
                <th className="py-1 pr-2">Reason</th>
                <th className="py-1 pr-2">By</th>
                <th className="py-1 pr-2">Since</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {holds.data!.holds.map((h) => (
                <tr key={h.userId} className="border-t border-border">
                  <td className="py-1 pr-2 font-mono text-xs">{h.userId}</td>
                  <td className="py-1 pr-2">{h.reason}</td>
                  <td className="py-1 pr-2 font-mono text-xs">{h.createdBy}</td>
                  <td className="py-1 pr-2 text-xs">{fmtWhen(h.createdAt)}</td>
                  <td className="py-1 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={release.isPending}
                      onClick={() => release.mutate(h.userId)}
                      aria-label={`Release hold for ${h.userId}`}
                    >
                      Release
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Forges table
// ============================================================================

type PendingAction = { kind: 'stop' | 'archive'; forge: ForgeSummary } | null;

function ForgesCard() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ForgeStatus | ''>('');
  const [userFilter, setUserFilter] = useState('');
  const [appliedUser, setAppliedUser] = useState('');
  const [offset, setOffset] = useState(0);
  const [pending, setPending] = useState<PendingAction>(null);

  const params: AdminForgeListParams = {
    ...(status ? { status } : {}),
    ...(appliedUser ? { userId: appliedUser } : {}),
    limit: PAGE_SIZE,
    offset,
  };
  const list = useQuery({
    queryKey: queryKeys.adminForge.forges(params),
    queryFn: () => api.adminForgeListForges(params),
  });

  const afterMutation = (title: string) => {
    toast({ title });
    void qc.invalidateQueries({ queryKey: queryKeys.adminForge.all });
  };
  const stop = useMutation({
    mutationFn: (forgeId: string) => api.adminForgeStop(forgeId),
    onSuccess: (data) => afterMutation(`Force-stopped ${shortId(data.forge.id)}`),
    onError: (err) =>
      toast({
        title: 'Could not stop forge',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  const archive = useMutation({
    mutationFn: (forgeId: string) => api.adminForgeArchive(forgeId),
    onSuccess: (data) => afterMutation(`Archived ${shortId(data.forge.id)}`),
    onError: (err) =>
      toast({
        title: 'Could not archive forge',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const confirmPending = () => {
    if (!pending) return;
    if (pending.kind === 'stop') stop.mutate(pending.forge.id);
    else archive.mutate(pending.forge.id);
    setPending(null);
  };

  const total = list.data?.total ?? 0;
  const forges = list.data?.forges ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Forges</CardTitle>
        <CardDescription>
          Every forge on the platform, newest activity first. Stop here is a FORCE stop.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          className="flex flex-wrap gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            setOffset(0);
            setAppliedUser(userFilter.trim());
          }}
        >
          <div>
            <Label htmlFor="forge-status-filter">Status</Label>
            <select
              id="forge-status-filter"
              value={status}
              onChange={(e) => {
                setOffset(0);
                setStatus(e.target.value as ForgeStatus | '');
              }}
              className="block h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All</option>
              {FORGE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="forge-user-filter">User id</Label>
            <Input
              id="forge-user-filter"
              value={userFilter}
              maxLength={64}
              placeholder="usr_…"
              onChange={(e) => setUserFilter(e.target.value)}
            />
          </div>
          <Button type="submit" variant="outline">
            Apply
          </Button>
          <span className="text-xs text-muted-foreground ml-auto" data-testid="forge-total">
            {total} matching
          </span>
        </form>

        {list.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading forges…</p>
        ) : list.isError ? (
          <p className="text-sm text-destructive">
            Could not load forges: {errorMessage(list.error)}
          </p>
        ) : forges.length === 0 ? (
          <p className="text-sm text-muted-foreground">No forges match.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2">Forge</th>
                  <th className="py-1 pr-2">User</th>
                  <th className="py-1 pr-2">Status</th>
                  <th className="py-1 pr-2">Size</th>
                  <th className="py-1 pr-2">Gen</th>
                  <th className="py-1 pr-2">Last active</th>
                  <th className="py-1 pr-2">Snapshot</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {forges.map((f) => (
                  <tr
                    key={f.id}
                    className="border-t border-border"
                    data-testid={`forge-row-${f.id}`}
                  >
                    <td className="py-1 pr-2 font-mono text-xs" title={f.id}>
                      {shortId(f.id)}
                      <div className="text-muted-foreground" title={f.archeId}>
                        {shortId(f.archeId)}
                      </div>
                    </td>
                    <td className="py-1 pr-2 font-mono text-xs">{f.userId}</td>
                    <td className="py-1 pr-2">
                      <Badge variant={statusVariant(f.status)}>{f.status}</Badge>
                      {f.billingHold && (
                        <Badge variant="destructive" className="ml-1">
                          billing hold
                        </Badge>
                      )}
                      {f.failureReason && (
                        <div
                          className="text-xs text-destructive max-w-xs truncate"
                          title={f.failureReason}
                        >
                          {f.failureReason}
                        </div>
                      )}
                    </td>
                    <td className="py-1 pr-2">{f.size}</td>
                    <td className="py-1 pr-2 tabular-nums">{f.runtimeGeneration}</td>
                    <td className="py-1 pr-2 text-xs">{fmtWhen(f.lastActiveAt)}</td>
                    <td className="py-1 pr-2 text-xs tabular-nums">
                      {formatBytes(f.snapshotBytes)}
                    </td>
                    <td className="py-1 text-right whitespace-nowrap">
                      {FORCE_STOPPABLE.has(f.status) && (
                        <Button
                          size="sm"
                          variant="destructive"
                          className="mr-1"
                          disabled={stop.isPending}
                          onClick={() => setPending({ kind: 'stop', forge: f })}
                          aria-label={`Stop ${f.id}`}
                        >
                          Stop
                        </Button>
                      )}
                      {ARCHIVABLE.has(f.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={archive.isPending}
                          onClick={() => setPending({ kind: 'archive', forge: f })}
                          aria-label={`Archive ${f.id}`}
                        >
                          Archive
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <Button
            size="sm"
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <span>
            {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}`} of {total}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === 'stop' ? 'Force stop this forge?' : 'Archive this forge?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.kind === 'stop'
                ? `This kills the task for ${pending.forge.id} (owner ${pending.forge.userId}) without draining. Anything the runtime has not snapshotted is lost. The action is audited.`
                : `${pending?.forge.id} keeps its snapshot but can no longer be started. The action is audited.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                pending?.kind === 'stop'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
              onClick={confirmPending}
            >
              {pending?.kind === 'stop' ? 'Force stop' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ============================================================================
// Page
// ============================================================================

export function ForgeAdminPage() {
  const metrics = useQuery({
    queryKey: queryKeys.adminForge.metrics(),
    queryFn: () => api.adminForgeGetMetrics(),
    refetchInterval: 30_000,
  });

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-8 space-y-6">
      <div>
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dev Console
        </Link>
        <h1 className="text-3xl font-bold text-foreground">Fias Forge</h1>
        <p className="mt-2 text-muted-foreground">
          Hosted Claude Code build environments — platform switches, fleet metrics, user holds, and
          every forge with force-stop and archive.
        </p>
      </div>

      <SwitchesCard />

      <section aria-label="Fleet metrics">
        {metrics.isError ? (
          <p className="text-sm text-destructive">
            Could not load metrics: {errorMessage(metrics.error)}
          </p>
        ) : (
          <MetricsTiles metrics={metrics.data?.metrics} />
        )}
      </section>

      <HoldsCard />
      <ForgesCard />
    </div>
  );
}

export default ForgeAdminPage;
